import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle, XCircle, Clock } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import type { ChargeHistoryItem } from '../../types';

const statusConfig: Record<string, { label: string; icon: React.ReactNode; classes: string }> = {
  paid: {
    label: 'Paid',
    icon: <CheckCircle className="w-4 h-4" />,
    classes: 'bg-green-50 text-green-700 border border-green-200',
  },
  failed: {
    label: 'Payment Failed',
    icon: <XCircle className="w-4 h-4" />,
    classes: 'bg-red-50 text-red-700 border border-red-200',
  },
  pending: {
    label: 'Awaiting Payment',
    icon: <Clock className="w-4 h-4" />,
    classes: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  exempted: {
    label: 'Exempted',
    icon: <CheckCircle className="w-4 h-4" />,
    classes: 'bg-gray-50 text-gray-600 border border-gray-200',
  },
};

const ChargeRow: React.FC<{ charge: ChargeHistoryItem }> = ({ charge }) => {
  const [retrying, setRetrying] = useState(false);

  const config = statusConfig[charge.payment_status] ?? statusConfig.pending;
  const amountDisplay = charge.amount != null
    ? `$${(charge.amount / 100).toFixed(2)}`
    : charge.payment_status === 'exempted' ? '—' : '—';
  const dateDisplay = charge.charged_at
    ? new Date(charge.charged_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const token = localStorage.getItem('authToken');
      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
        ? 'http://localhost:8000/api/'
        : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

      const response = await fetch(`${apiBase}stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pet_id: charge.pet_id }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error('Retry payment error:', err);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-gray-100 bg-gray-50">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-4 h-4 text-purple-500" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{charge.pet_name}</p>
          <p className="text-xs text-gray-500 capitalize">{charge.pet_type} · Registration Fee</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-sm font-semibold text-gray-800">{amountDisplay}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.classes}`}>
          {config.icon}
          {config.label}
        </span>
        <span className="text-xs text-gray-400 hidden sm:block">{dateDisplay}</span>
        {charge.payment_status === 'failed' && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium pmc-btn-primary rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {retrying ? (
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Retry'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const Charges: React.FC = () => {
  const [chargeHistory, setChargeHistory] = useState<ChargeHistoryItem[]>([]);
  const [loadingCharges, setLoadingCharges] = useState<boolean>(true);

  useEffect(() => {
    const loadChargeHistory = async () => {
      try {
        setLoadingCharges(true);
        const token = localStorage.getItem('authToken');
        const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

        const response = await fetch(`${apiBase}stripe/charge-history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setChargeHistory(data.charges || []);
        }
      } catch (error) {
        console.error('Error loading charge history:', error);
      } finally {
        setLoadingCharges(false);
      }
    };

    loadChargeHistory();
  }, []);

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 h-[73px] flex items-center flex-shrink-0">
        <div className="px-4 sm:px-5 w-full flex items-center justify-between">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Charges</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        <div className="min-h-full py-6 sm:py-8">
          <div className="max-w-2xl mx-auto px-4 sm:px-5 pb-20 sm:pb-32">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-600" />
                Charge History
              </h2>

              {loadingCharges ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="small" text="Loading charges..." />
                </div>
              ) : chargeHistory.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No charge history found.</p>
              ) : (
                <div className="space-y-3">
                  {chargeHistory.map((charge) => (
                    <ChargeRow key={charge.pet_id} charge={charge} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charges;
